import { lazy, Suspense, useState } from 'react';
import { act, formatNumber } from '../../lib/actions';
import { api } from '../../lib/api';
import { Button } from '../ui';
import { AddButton, Cell, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, useLoad, when } from './kit';

// The canvas library is only downloaded by admins who open a flow
const IvrEditor = lazy(() => import('./IvrEditor').then((m) => ({ default: m.IvrEditor })));

interface FlowRow {
  id: string;
  name: string;
  draft_updated_at: string;
  live_version: number | null;
  published_at: string | null;
  unpublished_changes: boolean;
  numbers: string[];
}

/** IVR flows: the list, and the canvas editor for the one that is open. */
export function IvrFlows() {
  const [flows, reload] = useLoad<FlowRow[]>('/ivr-flows');
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  if (open) {
    return (
      <Suspense fallback={<p className="text-sm text-ink-2">Loading the editor…</p>}>
        <IvrEditor
          id={open}
          onClose={() => {
            setOpen(null);
            reload();
          }}
        />
      </Suspense>
    );
  }

  return (
    <>
      <SectionHeader
        title="IVR flows"
        lead="What callers hear and press before they reach an agent. Build a flow, publish it, then point a number at it under Numbers."
        action={<AddButton onClick={() => setAdding(true)}>New flow</AddButton>}
      />
      <Table head={['Flow', 'Status', 'Numbers', 'Last edited']} empty={flows?.length === 0 && 'No IVR flows yet. Numbers ring their queue directly until you add one.'}>
        {(flows ?? []).map((f) => (
          <Row key={f.id} onClick={() => setOpen(f.id)}>
            <Cell className="font-medium">{f.name}</Cell>
            <Cell>
              <span className="flex flex-wrap gap-1.5">
                {f.live_version ? <Pill tone="good">Live: version {f.live_version}</Pill> : <Pill tone="warn">Not published</Pill>}
                {f.live_version && f.unpublished_changes ? <Pill>Unpublished changes</Pill> : null}
              </span>
            </Cell>
            <Cell className="tabular">{f.numbers.length ? f.numbers.map(formatNumber).join(', ') : <span className="text-ink-3">None</span>}</Cell>
            <Cell className="text-ink-2">{when(f.draft_updated_at)}</Cell>
          </Row>
        ))}
      </Table>
      {adding ? (
        <NewFlowDrawer
          onClose={() => setAdding(false)}
          onDone={(id) => {
            setAdding(false);
            setOpen(id);
          }}
        />
      ) : null}
    </>
  );
}

function NewFlowDrawer({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    const r = await act(() => api<{ id: string }>('POST', '/ivr-flows', { name: name.trim() }));
    setBusy(false);
    if (r) onDone(r.id);
  }
  return (
    <Drawer
      title="New IVR flow"
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" onClick={() => void save()} disabled={busy || name.trim().length < 2}>
            {busy ? 'Creating…' : 'Create and open'}
          </Button>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <Field label="Name" hint="For example: Main line, After hours, Sales hotline.">
        {(id) => (
          <input
            id={id}
            className={inputClass}
            value={name}
            maxLength={60}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && name.trim().length >= 2 && void save()}
          />
        )}
      </Field>
    </Drawer>
  );
}
